import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CaseDocuments from "@/components/CaseDocuments";
import CaseMessages from "@/components/CaseMessages";
import CaseTreatments from "@/components/CaseTreatments";
import CaseStagePipeline from "@/components/CaseStagePipeline";
import AttorneyAssignSelect from "@/components/dashboard/AttorneyAssignSelect";
import ComplianceCard from "@/components/ComplianceCard";
import TasksList from "@/components/TasksList";
import TreatmentPlanDialog from "@/components/TreatmentPlanDialog";
import { TempPill, StatusPill } from "./Pills";
import { ChevronDown, Trash2, Mail, Phone, MapPin, Calendar, X, Sparkles, Users, Stethoscope, Percent } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "closed", "rejected"];

export default function LeadDetailDialog({ selected, onClose, onUpdateStatus, onRemove, onRescore, rescoring, onAttorneyAssigned }) {
  const [showPlan, setShowPlan] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <Dialog open={!!selected} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail">
        {selected && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black tracking-tight text-[#11322A]">
                {selected.full_name}
              </DialogTitle>
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <StatusPill status={selected.status} />
                <TempPill temperature={selected.temperature} score={selected.temperature_score} />
                <span className="pill"><MapPin className="h-3 w-3" /> {selected.state}{selected.city ? ` · ${selected.city}` : ""}</span>
                <span className="pill"><Calendar className="h-3 w-3" /> {selected.accident_date || "no date"}</span>
                {selected.notification_sent && <span className="pill" style={{ color: "#166534", borderColor: "#bce5cc", background: "#E6F6ED" }}>email sent</span>}
                {selected.sms_sent && <span className="pill" style={{ color: "#1d4ed8", borderColor: "#b3d4ff", background: "#E6F3FF" }}>after-hours SMS sent</span>}
                {selected.hot_alert_sent && <span className="pill" style={{ color: "#B91C1C", borderColor: "#F5B6B0", background: "#FDE8E6" }}>🔥 firm alerted</span>}
              </div>
            </DialogHeader>

            {selected.temperature_reasoning && (
              <div className="mt-3 flex items-start gap-3 rounded-xl border border-[#E0E4E2] bg-[#FDFBF7] p-4" data-testid="ai-reasoning">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white text-[#11322A]" style={{ border: "1px solid var(--border)" }}>
                  <Sparkles className="h-4 w-4" />
                </div>
                <div className="flex-1 text-sm">
                  <div className="label-cap text-[10px]">Avery's read on this case</div>
                  <div className="mt-1 text-[#0B1317]">{selected.temperature_reasoning}</div>
                </div>
                <button
                  onClick={() => onRescore(selected.id)}
                  disabled={rescoring}
                  className="inline-flex items-center gap-1 rounded-md border border-[#E0E4E2] bg-white px-2 py-1 text-xs font-bold text-[#11322A] hover:border-[#11322A] disabled:opacity-50"
                  data-testid="rescore-btn"
                >
                  {rescoring ? "…" : "Re-score"}
                </button>
              </div>
            )}

            <Tabs defaultValue="overview" className="mt-4">
              <TabsList className="w-full justify-start" data-testid="admin-case-tabs">
                <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="compliance" data-testid="admin-tab-compliance">Compliance</TabsTrigger>
                <TabsTrigger value="tasks" data-testid="admin-tab-tasks">Tasks</TabsTrigger>
                <TabsTrigger value="treatments" data-testid="admin-tab-treatments">Treatments</TabsTrigger>
                <TabsTrigger value="documents" data-testid="admin-tab-documents">Documents</TabsTrigger>
                <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <a href={`mailto:${selected.email}`} className="card-soft p-3 hover:border-[#11322A]" data-testid="lead-email">
                    <div className="label-cap text-[10px]">Email</div>
                    <div className="mt-1 flex items-center gap-2 text-sm font-bold text-[#0B1317]"><Mail className="h-3.5 w-3.5" />{selected.email}</div>
                  </a>
                  <a href={`tel:${selected.phone}`} className="card-soft p-3 hover:border-[#11322A]" data-testid="lead-phone">
                    <div className="label-cap text-[10px]">Phone</div>
                    <div className="mt-1 flex items-center gap-2 text-sm font-bold text-[#0B1317]"><Phone className="h-3.5 w-3.5" />{selected.phone}</div>
                  </a>
                </div>

                <div className="mt-4">
                  <AttorneyAssignSelect lead={selected} onAssigned={onAttorneyAssigned} />
                </div>

                <div className="mt-4 card-soft p-4">
                  <div className="label-cap text-[10px]">Case stage</div>
                  <div className="mt-3">
                    <CaseStagePipeline current={selected.case_stage || "intake"} />
                  </div>
                </div>

                <div className="mt-4 grid gap-4 sm:grid-cols-2 text-sm">
                  <div><div className="label-cap text-[10px]">Accident type</div><div className="mt-1">{selected.accident_type || "—"}</div></div>
                  <div><div className="label-cap text-[10px]">At fault</div><div className="mt-1">{selected.at_fault || "—"}</div></div>
                  <div><div className="label-cap text-[10px]">Police report</div><div className="mt-1">{selected.police_report ? "Yes" : "No"}</div></div>
                  <div><div className="label-cap text-[10px]">Medical treatment</div><div className="mt-1">{selected.medical_treatment ? "Yes" : "No"}</div></div>
                  <div className="sm:col-span-2"><div className="label-cap text-[10px]">Injuries</div><div className="mt-1 text-[#0B1317]">{selected.injuries || "—"}</div></div>
                  {selected.description && (
                    <div className="sm:col-span-2"><div className="label-cap text-[10px]">Notes</div><div className="mt-1 whitespace-pre-wrap text-[#0B1317]">{selected.description}</div></div>
                  )}
                </div>

                {/* Referral attribution */}
                {(selected.referrer_name || selected.referral_source_type) && (
                  <div className="mt-4 card-soft p-4" data-testid="lead-referral-info">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="label-cap text-[10px]">Referral source</div>
                        <div className="mt-1 inline-flex items-center gap-2 text-sm font-bold text-[#11322A]">
                          <Users className="h-3.5 w-3.5 text-[#E85D04]" />
                          {selected.referrer_name || "—"}
                        </div>
                        {selected.referral_source_type && (
                          <div className="mt-0.5 text-[11px] uppercase tracking-wider text-[#5C6B68]">
                            {selected.referral_source_type.replace(/_/g, " ")}
                          </div>
                        )}
                      </div>
                      {selected.referral_commission_pct != null && (
                        <div className="text-right">
                          <div className="label-cap text-[10px]">Commission</div>
                          <div className="mt-1 inline-flex items-center font-display text-lg font-black text-[#11322A]">
                            {selected.referral_commission_pct}
                            <Percent className="h-3.5 w-3.5" />
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-[#5C6B68]">
                            {selected.referral_status || "n/a"}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="compliance" className="mt-4">
                <ComplianceCard caseId={selected.id} variant="admin" refreshKey={refreshKey} />
                <button
                  onClick={() => setShowPlan(true)}
                  className="btn-secondary mt-3 text-sm"
                  data-testid="admin-set-plan"
                >
                  <Stethoscope className="h-4 w-4" /> {selected.treatment_plan_visits_per_week ? "Edit treatment plan" : "Set treatment plan"}
                </button>
              </TabsContent>

              <TabsContent value="tasks" className="mt-4">
                <TasksList caseId={selected.id} scope="admin" onChange={() => setRefreshKey((k) => k + 1)} />
              </TabsContent>

              <TabsContent value="treatments" className="mt-4">
                <CaseTreatments caseId={selected.id} scope="admin" />
              </TabsContent>

              <TabsContent value="documents" className="mt-4">
                <CaseDocuments caseId={selected.id} scope="admin" />
              </TabsContent>

              <TabsContent value="messages" className="mt-4">
                <CaseMessages caseId={selected.id} scope="admin" />
              </TabsContent>
            </Tabs>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#E0E4E2] pt-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="btn-secondary !py-2 text-sm" data-testid="lead-status-trigger">
                    Set status <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {STATUSES.map((s) => (
                    <DropdownMenuItem key={s} onClick={() => onUpdateStatus(selected.id, s)} data-testid={`lead-status-${s}`}>
                      Mark as {s}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="flex items-center gap-2">
                <button onClick={() => onRemove(selected.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-[#F5B6B0] bg-white px-3 py-2 text-sm font-bold text-[#B91C1C] hover:bg-[#FDE8E6]" data-testid="lead-delete">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
                <button onClick={onClose} className="inline-flex items-center gap-1.5 rounded-lg bg-[#11322A] px-3 py-2 text-sm font-bold text-white hover:bg-[#0a221c]" data-testid="lead-close">
                  <X className="h-3.5 w-3.5" /> Close
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
      {selected && (
        <TreatmentPlanDialog
          open={showPlan}
          onOpenChange={setShowPlan}
          lead={selected}
          scope="admin"
          onSaved={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </Dialog>
  );
}
